import { Button, ChipProps, TableCell, TableRow } from '@mui/material';
import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OfferFilter } from 'src/components/dialogs/OfferFilterDialog';
import { BrickwiseTable } from 'src/components/mui-overrides/BrickwiseTable';
import { fetchOffers } from 'src/hooks/useOffers';
import { setStateQueryParameter } from 'src/hooks/useStateQueryParams';
import { Offer, OffersResponse } from 'src/models/offers';
import { cellStyle } from './UserDetailSite';
import { getUsersWithQuery, postDeleteAcceptedOffer } from 'src/api/oApi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAssets } from 'src/hooks/useAssets';
import { CopyToClipboardButton } from 'src/components/ui/CopyToClipboardButton';
import { DetailDrawer } from 'src/components/DetailDrawer';
import { DetailView, formatDetailDate } from 'src/components/DetailView';
import { ConfirmationDialog } from 'src/components/ui/ConfirmationDialog';
import { generalStore } from 'src/stores/GeneralStore';

export const OfferSite = () => {
  const [page, setPage] = setStateQueryParameter<number>(0, 'page', 'number');
  const assets = useAssets();
  const [offerDetail, setOfferDetail] = useState<Offer | null>(null);
  const [offers, setOffers] = useState<OffersResponse | null>(null);
  const lastFilter = useRef<any>({});
  offers?.offers.map((offer) => {
    offer.assetTitle = assets?.find((asset) => asset.id === offer.assetId)?.title;
    offer.assetStage = assets?.find((asset) => asset.id === offer.assetId)?.stage as Offer['assetStage'];
  });
  return (
    <>
      <OfferFilter
        submit={(values: any) => {
          values.page = page;
          lastFilter.current = values;
          fetchOffers(values, setOffers);
          setOfferDetail(null);
        }}
        setPage={setPage}
        page={page}
      />
      <OfferList page={page} offers={offers} setPage={setPage} setOfferDetail={setOfferDetail} offerDetail={offerDetail} />
      <DetailDrawer open={!!offerDetail} onClose={() => setOfferDetail(null)} title="Offer">
        {offerDetail ? (
          <OfferDetail
            offer={offerDetail}
            onReversed={() => {
              setOfferDetail(null);
              fetchOffers({ ...lastFilter.current, page }, setOffers);
            }}
          />
        ) : null}
      </DetailDrawer>
    </>
  );
};

const rowsPerPage = 10;

export function OfferList({
  offers,
  page,
  offerDetail,
  setOfferDetail,
  setPage,
}: {
  page: number;
  offers: OffersResponse | null;
  setPage: any;
  offerDetail?: any;
  setOfferDetail: any;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  /** Fake pagination. Could be implemented as a real pagination in the future. */
  // slice (not splice) so re-renders (e.g. opening the detail drawer) don't mutate the offers state.
  const shownOffers = offers?.offers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) || [];
  return (
    <BrickwiseTable
      page={page}
      rowsPerPage={rowsPerPage}
      hidePagination={false}
      hideChangePageLengthChange={false}
      length={offers?.count ?? 0}
      setPage={setPage}
      renderHeader={() => {
        return (
          <TableRow>
            <TableCell>{t('offers.table.id')}</TableCell>
            <TableCell>{t('offers.table.assetId')}</TableCell>
            <TableCell>Asset Title</TableCell>
            <TableCell>Asset Stage</TableCell>
            <TableCell>{t('offers.table.firstParty')}</TableCell>
            <TableCell>{t('offers.table.secondParty')}</TableCell>
            <TableCell>{t('offers.table.status')}</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>{t('offers.table.amount')}</TableCell>
            <TableCell>{t('offers.table.price')}</TableCell>
            <TableCell>CreatedAt</TableCell>
            <TableCell>UpdatedAt</TableCell>
          </TableRow>
        );
      }}
      renderRows={() => {
        return (
          <>
            {shownOffers.map((row, index) => {
              return (
                <TableRow
                  onClick={() => setOfferDetail(row)}
                  key={index}
                  hover
                  style={{
                    cursor: 'pointer',
                    textDecoration: 'none',
                    backgroundColor: row?._id === offerDetail?._id ? 'oldlace' : 'white',
                  }}
                >
                  <TableCell>
                    <CopyToClipboardButton text={row.offerId} label={row.offerId} />
                  </TableCell>
                  <TableCell>{row.assetId}</TableCell>
                  <TableCell>{row.assetTitle}</TableCell>
                  <TableCell>{row.assetStage}</TableCell>
                  <TableCell>
                    {row.firstParty ? (
                      <Button
                        variant="contained"
                        color="primary"
                        style={{ maxWidth: '80px' }}
                        onClick={async () => await navigateToUserByWallet(row.firstParty, navigate)}
                      >
                        <span style={{ ...cellStyle, maxWidth: '100px' }}>{row.firstParty}</span>
                      </Button>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {row.secondParty ? (
                      <Button
                        variant="contained"
                        color="primary"
                        style={{ maxWidth: '80px' }}
                        onClick={async () => await navigateToUserByWallet(row.secondParty, navigate)}
                      >
                        <span style={{ ...cellStyle, maxWidth: '100px' }}>{row.secondParty}</span>
                      </Button>
                    ) : null}
                  </TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>{row.type}</TableCell>
                  <TableCell>{row.amount?.toString()}</TableCell>
                  <TableCell>{row.price?.toString()}</TableCell>
                  <TableCell>{row.createdAt}</TableCell>
                  <TableCell>{row.updatedAt}</TableCell>
                </TableRow>
              );
            })}
          </>
        );
      }}
    />
  );
}

const offerStatusChipColor = (status?: string): ChipProps['color'] => {
  switch (status) {
    case 'accepted':
      return 'warning';
    case 'new':
    case 'open':
      return 'info';
    case 'closed':
      return 'success';
    case 'deleted':
      return 'error';
    default:
      return 'default';
  }
};

const WalletPartyButton: FC<{ wallet?: string }> = ({ wallet }) => {
  const navigate = useNavigate();
  if (!wallet) return null;
  return (
    <Button
      variant="contained"
      color="primary"
      style={{ maxWidth: '220px' }}
      onClick={async () => await navigateToUserByWallet(wallet, navigate)}
    >
      <span style={{ ...cellStyle, maxWidth: '200px' }}>{wallet}</span>
    </Button>
  );
};

const OfferDetail: FC<{ offer: Offer; onReversed: () => void }> = ({ offer, onReversed }) => {
  const [reverseConfirmationOpen, setReverseConfirmationOpen] = useState(false);

  const reverseAcceptedOffer = async () => {
    try {
      generalStore.setIsLoading(true);
      await postDeleteAcceptedOffer({ offerId: offer.offerId });
      generalStore.setMessage('The offer now needs to be deleted on the blockchain.');
      setReverseConfirmationOpen(false);
      onReversed();
    } catch (error: any) {
      generalStore.setError(
        error?.response?.data?.error?.message || error?.response?.data?.error || error?.message || 'Error on Operation',
      );
    } finally {
      generalStore.setIsLoading(false);
    }
  };

  return (
    <>
      <ConfirmationDialog
        title="Reverse this accepted offer?"
        submitBtnLabel="Reverse"
        isOpened={reverseConfirmationOpen}
        close={() => setReverseConfirmationOpen(false)}
        onSubmit={reverseAcceptedOffer}
      />
      <DetailView
        chips={[
          { label: offer.type, color: 'default' },
          { label: offer.status, color: offerStatusChipColor(offer.status) },
          { label: offer.assetStage, color: 'default' },
        ]}
        headline={offer.assetTitle || offer.assetId}
        subheadline={[offer.offerId ? `Offer ${offer.offerId}` : undefined, formatDetailDate(offer.createdAt)].filter(Boolean).join(' — ')}
        actions={
          offer.status === 'accepted' ? (
            <Button variant="contained" color="error" onClick={() => setReverseConfirmationOpen(true)}>
              Reverse accepted offer
            </Button>
          ) : null
        }
        sections={[
          {
            title: 'Overview',
            fields: [
              {
                label: 'Offer ID',
                value: offer.offerId ? <CopyToClipboardButton text={offer.offerId} label={offer.offerId} /> : undefined,
              },
              {
                label: 'Asset ID',
                value: offer.assetId ? <CopyToClipboardButton text={offer.assetId} label={offer.assetId} /> : undefined,
              },
              { label: 'Asset title', value: offer.assetTitle },
              { label: 'Asset stage', value: offer.assetStage },
              { label: 'Amount', value: offer.amount },
              { label: 'Price', value: offer.price !== undefined && offer.price !== null ? `${offer.price} €` : undefined },
              { label: 'Payment type', value: offer.paymentType },
              { label: 'Contact type', value: offer.contactType },
              { label: 'Token ID', value: offer.tokenId },
              { label: 'Created at', value: formatDetailDate(offer.createdAt) },
              { label: 'Updated at', value: formatDetailDate(offer.updatedAt) },
            ],
          },
          {
            title: 'Parties',
            fields: [
              { label: 'First party', value: offer.firstParty ? <WalletPartyButton wallet={offer.firstParty} /> : undefined },
              { label: 'Second party', value: offer.secondParty ? <WalletPartyButton wallet={offer.secondParty} /> : undefined },
              {
                label: 'Interested party',
                value: offer.interestedParty ? <WalletPartyButton wallet={offer.interestedParty} /> : undefined,
              },
            ],
          },
        ]}
        raw={offer}
      />
    </>
  );
};

const navigateToUserByWallet = async (wallet: string, navigate: any) => {
  const response = await getUsersWithQuery({ wallet: wallet });
  if (response?.data?.data.count > 0) {
    const userId = response?.data?.data.users[0].userId;
    navigate(`/user/${userId}?userDetail_tab=0`);
    // we have to set user param:
    const [searchParams, setSearchParams] = useSearchParams();
    setSearchParams({ ...searchParams, userDetail_tab: '0' });
  }
};
